// IssueEditor — Phase 2d methodology-aware branch.
//
// What changed vs Phase 2c:
//   - Live Definition-of-Ready meter renders the design's four checks
//     (title / description / outcome / acceptance criteria). The
//     server-side rubric is authoritative and runs at save time; this
//     client meter is fast/local feedback only.
//   - "Find related work" auto-fire 800 ms after the user stops editing
//     the title/description. Results render in `ProposalsPanel` above the
//     save button; the user toggles Accept/Skip per row.
//   - New-issue Save opens an SSE connection to `POST /issues`. The
//     editor renders the server's phase events as a step indicator and
//     closes when `done` arrives.
//   - Edit Save still PATCHes JSON — the server re-runs DoR when the
//     body / AC changed.
//   - For existing issues, an `IssueTimeline` strip renders below AC.
//
// Persistence:
//   - new   → POST  /api/projects/:id/issues   (SSE; methodology-aware)
//   - edit  → PATCH /api/projects/:id/issues/:iid (JSON)

import { useEffect, useMemo, useRef, useState, type JSX } from "react";

import {
  type AcceptedLink,
  type ComplexityScore,
  type DashboardIssue,
  type DashboardMilestone,
  type DecomposeChild,
  type IssueEditorPayload,
  type Priority,
} from "@loom/shared/dashboard";

import { useT, type MessageKey } from "../../i18n/index.js";
import { AutoGrowTextarea } from "../../ui/AutoGrowTextarea";
import {
  buildDorChecks,
  NEW_DRAFT_DEFAULTS,
  parseBody,
} from "../issueDraft";
import { useCreateIssue, type CreateIssueProgress } from "../useCreateIssue";
import { useIssueTimeline } from "../useIssueTimeline";
import { useProposeLinks } from "../useProposeLinks";
import { useUpdateIssue } from "../useUpdateIssue";

import { Icon } from "./Icon";
import { IssueTimeline } from "./IssueTimeline";
import { ProposalsPanel } from "./ProposalsPanel";
import { PRIORITY_META } from "./SteerRow";

export type IssueEditorFocusField = "title" | "outcome" | "acceptance_criteria";
export type IssueEditorTab = "fields" | "proposals";

/** Phase 2d slot — supply this to enable the methodology-aware branch. */
export interface IntakeBranch {
  readonly draftId: string;
}

interface IssueEditorProps {
  readonly projectId: number;
  readonly issue: DashboardIssue | null;
  readonly focusField?: IssueEditorFocusField;
  readonly onClose: () => void;
  readonly onSaved: (
    issue: DashboardIssue,
    /** Set by the intake apply path for newly-created issues so the page can
     *  surface the "make this a milestone?" suggestion. */
    complexity?: ComplexityScore,
  ) => void;
  readonly intakeBranch?: IntakeBranch;
  /** Phase 3b — opens directly on the proposals tab when set to
   *  "proposals". Defaults to "fields". */
  readonly initialTab?: IssueEditorTab;
  /** Phase 3b — when present, the proposals tab renders the supplied
   *  decompose children inside `ProposalsPanel` (children mode). */
  readonly childrenProposals?: ReadonlyArray<DecomposeChild>;
  /** Active project milestones surfaced as the dropdown options for the
   *  milestone selector. Edit-mode only; absent when the parent doesn't
   *  pre-fetch the roadmap. */
  readonly milestones?: ReadonlyArray<DashboardMilestone>;
  /** Wire that issues PUT /api/projects/:id/issues/:iid/milestone. Null
   *  means "detach". Called on every change of the dropdown. */
  readonly onAssignMilestone?: (
    iid: number,
    milestoneId: number | null,
  ) => Promise<void>;
}

const PROGRESS_KEY: Record<CreateIssueProgress["phase"], MessageKey> = {
  propose_links: "dashboard.progress.propose_links",
  dor_check: "dashboard.progress.dor_check",
  creating: "dashboard.progress.creating",
};

export const IssueEditor = ({
  projectId,
  issue,
  focusField = "title",
  onClose,
  onSaved,
  intakeBranch,
  initialTab = "fields",
  childrenProposals,
  milestones,
  onAssignMilestone,
}: IssueEditorProps): JSX.Element => {
  const t = useT();
  void intakeBranch;

  const isNew = issue === null;
  const parsed = issue ? parseBody(issue.description) : NEW_DRAFT_DEFAULTS;
  const [title, setTitle] = useState(issue?.title ?? "");
  const [description, setDescription] = useState(parsed.description);
  const [outcome, setOutcome] = useState(parsed.outcome);
  const [ac, setAc] = useState<string[]>(
    parsed.acceptanceCriteria.length > 0 ? parsed.acceptanceCriteria : [""],
  );
  const [priority, setPriority] = useState<Priority>(issue?.priority ?? "normal");
  const [accepted, setAccepted] = useState<AcceptedLink[]>([]);
  const [acceptedChildren, setAcceptedChildren] = useState<number[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [milestoneAssigning, setMilestoneAssigning] = useState<boolean>(false);
  const [milestoneError, setMilestoneError] = useState<string | null>(null);
  // Local mirror of the milestone selection. The editor's `issue` prop is a
  // snapshot at open time, so it never reflects an attach/detach made via
  // this dropdown. We seed from the prop and update optimistically — the
  // parent's refetch will reconcile if the PUT fails.
  const [milestoneSelection, setMilestoneSelection] = useState<number | null>(
    issue?.milestone?.id ?? null,
  );
  const proposalsRef = useRef<HTMLDivElement | null>(null);

  const titleRef = useRef<HTMLTextAreaElement | null>(null);
  const outcomeRef = useRef<HTMLTextAreaElement | null>(null);
  const firstAcRef = useRef<HTMLTextAreaElement | null>(null);

  const create = useCreateIssue(projectId);
  const update = useUpdateIssue(projectId);
  const propose = useProposeLinks(projectId);
  const timeline = useIssueTimeline(projectId, issue?.iid ?? null);
  const saving = create.saving || update.saving;

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (initialTab === "proposals" && proposalsRef.current) {
        proposalsRef.current.scrollIntoView({ block: "nearest" });
        return;
      }
      if (focusField === "outcome") outcomeRef.current?.focus();
      else if (focusField === "acceptance_criteria") firstAcRef.current?.focus();
      else titleRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [focusField, initialTab]);

  const setAcAt = (idx: number, value: string): void =>
    setAc((list) => list.map((x, i) => (i === idx ? value : x)));
  const addAc = (): void => setAc((list) => [...list, ""]);
  const removeAc = (idx: number): void =>
    setAc((list) => (list.length > 1 ? list.filter((_, i) => i !== idx) : list));

  // Collapse ALL whitespace in the title, not just the ends. A GitLab issue
  // title is single-line by contract, and `.trim()` never touches interior
  // whitespace — so a line break pasted out of a wrapped document survived into
  // the title and broke every markdown structure Loom composes out of it.
  const cleanTitle = (): string =>
    title.replace(/\s+/gu, " ").trim() || "Untitled request";

  const buildPayload = (): IssueEditorPayload => ({
    title: cleanTitle(),
    description,
    outcome,
    acceptanceCriteria: ac.map((s) => s.trim()).filter((s) => s.length > 0),
    priority,
  });

  // Live DoR meter — recomputed every render off the local state.
  const checks = useMemo(
    () => buildDorChecks(title, description, outcome, ac),
    [title, description, outcome, ac],
  );
  const passed = checks.filter((c) => c.ok).length;
  const pct = Math.round((passed / checks.length) * 100);
  const ready = passed === checks.length;

  // Debounced auto-fire for the propose-links route. Only active in
  // new-issue mode — edit doesn't propose links.
  const acKey = ac.join("\n");
  useEffect(() => {
    if (!isNew) return;
    if (title.trim().length < 6) return;
    const snapshot: IssueEditorPayload = {
      title: cleanTitle(),
      description,
      outcome,
      acceptanceCriteria: ac.map((s) => s.trim()).filter((s) => s.length > 0),
      priority,
    };
    const t = window.setTimeout(() => {
      void propose.submit(snapshot);
    }, 800);
    return () => window.clearTimeout(t);
  }, [isNew, title, description, outcome, acKey, priority, ac, propose]);

  const toggleAccepted = (link: AcceptedLink): void => {
    setAccepted((list) => {
      const at = list.findIndex((l) => l.targetIid === link.targetIid);
      if (at >= 0) return list.filter((_, i) => i !== at);
      return [...list, link];
    });
  };

  const save = async (): Promise<void> => {
    setSubmitError(null);
    try {
      const payload = buildPayload();
      if (isNew) {
        const result = await create.submit(payload, accepted);
        onSaved(result.issue, result.complexity);
      } else {
        const next = await update.update(issue.iid, payload);
        onSaved(next);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  };

  const methodologySkipped =
    create.result?.methodologySkipped === true ||
    propose.methodologySkipped === true;

  return (
    <div className="overlay editor-overlay" onClick={onClose}>
      <div className="editor" onClick={(e) => e.stopPropagation()}>
        <div className="editor-head">
          <div>
            <div className="editor-eyebrow">
              {isNew ? t("dashboard.editor.newRequest") : t("dashboard.editor.editing", { key: issue.key })}
            </div>
            <div className="editor-headline">
              {isNew ? t("dashboard.editor.tellLoom") : t("dashboard.editor.clarify")}
            </div>
          </div>
          <button className="icon-close" onClick={onClose} aria-label={t("common.close")}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="editor-body">
          <label className="fld">
            <span className="fld-label">{t("dashboard.editor.fieldTitle")}</span>
            <AutoGrowTextarea
              ref={titleRef}
              className="fld-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("dashboard.editor.titlePlaceholder")}
            />
          </label>

          <label className="fld">
            <span className="fld-label">
              {t("dashboard.editor.whatYouNeed")} <span className="fld-hint">{t("dashboard.editor.hintOwnWords")}</span>
            </span>
            <AutoGrowTextarea
              className="fld-input"
              minLines={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("dashboard.editor.descriptionPlaceholder")}
            />
          </label>

          <label className="fld">
            <span className="fld-label">
              {t("dashboard.editor.businessOutcome")} <span className="fld-hint">{t("dashboard.editor.hintWhyMatters")}</span>
            </span>
            <AutoGrowTextarea
              ref={outcomeRef}
              className="fld-input"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              placeholder={t("dashboard.editor.outcomePlaceholder")}
            />
          </label>

          <div className="fld">
            <span className="fld-label">
              {t("dashboard.editor.acceptanceCriteria")}{" "}
              <span className="fld-hint">{t("dashboard.editor.hintHowDone")}</span>
            </span>
            <div className="ac-list">
              {ac.map((a, i) => (
                <div key={i} className="ac-item">
                  <span className="ac-bullet">{i + 1}</span>
                  <AutoGrowTextarea
                    ref={i === 0 ? firstAcRef : undefined}
                    className="ac-input"
                    value={a}
                    onChange={(e) => setAcAt(i, e.target.value)}
                    placeholder={t("dashboard.editor.acPlaceholder")}
                  />
                  {ac.length > 1 && (
                    <button
                      className="ac-remove"
                      onClick={() => removeAc(i)}
                      title={t("common.remove")}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button className="ac-add" onClick={addAc}>
              <Icon name="plus" size={12} /> {t("dashboard.editor.addCriterion")}
            </button>
          </div>

          {!isNew && (
            <div className="fld">
              <span className="fld-label">{t("dashboard.editor.history")}</span>
              <IssueTimeline events={timeline.events} loading={timeline.loading} />
            </div>
          )}

          <div className="fld">
            <span className="fld-label">{t("dashboard.editor.priority")}</span>
            <div className="prio-seg">
              {(Object.entries(PRIORITY_META) as Array<
                [Priority, (typeof PRIORITY_META)[Priority]]
              >).map(([k, v]) => (
                <button
                  key={k}
                  className={`prio-opt ${priority === k ? "on" : ""}`}
                  onClick={() => setPriority(k)}
                >
                  <span
                    className="prio-dot"
                    style={{ background: v.color }}
                  />
                  {t(v.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {!isNew && milestones && onAssignMilestone && issue && (
            <label className="fld" data-testid="issue-editor-milestone">
              <span className="fld-label">
                {t("dashboard.editor.milestone")}{" "}
                <span className="fld-hint">{t("dashboard.editor.hintGroupStories")}</span>
              </span>
              <select
                className="fld-input"
                value={milestoneSelection ?? ""}
                disabled={milestoneAssigning}
                onChange={(e) => {
                  const raw = e.target.value;
                  const next = raw === "" ? null : Number.parseInt(raw, 10);
                  const prev = milestoneSelection;
                  setMilestoneSelection(next);
                  setMilestoneError(null);
                  setMilestoneAssigning(true);
                  onAssignMilestone(issue.iid, next)
                    .catch((err: unknown) => {
                      // Roll back the optimistic update so the dropdown
                      // doesn't lie about persisted state.
                      setMilestoneSelection(prev);
                      setMilestoneError(
                        err instanceof Error ? err.message : String(err),
                      );
                    })
                    .finally(() => setMilestoneAssigning(false));
                }}
              >
                <option value="">{t("dashboard.editor.noMilestone")}</option>
                {milestones.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                    {m.state === "closed" ? t("dashboard.editor.closedSuffix") : ""}
                  </option>
                ))}
              </select>
              {milestoneError && (
                <span className="form-error is-sm">{milestoneError}</span>
              )}
            </label>
          )}

          {isNew && (
            <ProposalsPanel
              mode="links"
              candidates={propose.candidates}
              accepted={accepted}
              loading={propose.loading}
              methodologySkipped={propose.methodologySkipped}
              onToggle={toggleAccepted}
            />
          )}

          {childrenProposals !== undefined && (
            <div ref={proposalsRef}>
              <ProposalsPanel
                mode="children"
                children={childrenProposals}
                accepted={acceptedChildren}
                loading={false}
                methodologySkipped={false}
                onToggleChild={(i) =>
                  setAcceptedChildren((cur) =>
                    cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i],
                  )
                }
              />
            </div>
          )}
        </div>

        <div className="editor-foot">
          <div className="dor">
            <div className="dor-top">
              <span className={`dor-state ${ready ? "ok" : ""}`}>
                <Icon name={ready ? "check" : "spark"} size={12} />
                {ready ? t("dashboard.editor.readyToBuild") : t("dashboard.editor.readinessPct", { pct })}
              </span>
              <span className="dor-sub">
                {ready
                  ? t("dashboard.editor.meetsDor")
                  : t("dashboard.editor.checksProgress", { passed, total: checks.length })}
              </span>
            </div>
            <div className="dor-bar">
              <div
                className="dor-fill"
                style={{
                  width: `${pct}%`,
                  background: ready ? "var(--gate-pass)" : "var(--st-progress)",
                }}
              />
            </div>
            <div className="dor-checks">
              {checks.map((c, i) => (
                <span key={i} className={`dor-check ${c.ok ? "ok" : ""}`}>
                  <Icon name={c.ok ? "check" : "open"} size={11} />
                  {t(c.labelKey)}
                </span>
              ))}
            </div>
          </div>

          {methodologySkipped && (
            <div className="methodology-skipped">
              <Icon name="shield" size={11} />
              {t("dashboard.editor.dorOff")}
            </div>
          )}

          {isNew && create.saving && create.progress && (
            <div className="intake-progress">
              <Icon name="spark" size={12} />
              <span>
                {create.progress.message ?? t(PROGRESS_KEY[create.progress.phase])}
              </span>
            </div>
          )}

          {submitError && (
            <div className="form-error is-sm editor-submit-error">{submitError}</div>
          )}
          <div className="editor-actions">
            <button className="btn-ghost-lg" onClick={onClose} disabled={saving}>
              {t("dashboard.editor.cancel")}
            </button>
            <button
              className="start-btn editor-save"
              onClick={() => void save()}
              disabled={saving}
            >
              <Icon name="check" size={14} />
              {saving
                ? t("dashboard.editor.saving")
                : isNew
                  ? t("dashboard.editor.createRequest")
                  : t("dashboard.editor.saveChanges")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
